import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { object, string, date } from "yup";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import AdminFormModal from "../../../AdminFormModal";
import { addToast } from "../../../../helpers";
import api from "../../../../providers/api";

import "./style.scss";

const { Field } = Form;

const validationSchema = object().shape({
  ArabicName: string().required(" "),
  EnglishName: string().required(" "),
  Likelihood: object().required(" ").nullable(),
  Impact: object().required(" ").nullable(),
  Category: object().required(" ").nullable(),
  Status: object().required(" ").nullable(),
  DueDate: date().required(" "),
  MitigationPlan: string().required(" "),
});

const RiskForm = ({
  data,
  closeModal,
  risks,
  setRisks,
  initiativeData = null,
  getData = () => {},
}) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    ArabicName: "",
    EnglishName: "",
    Likelihood: null,
    Impact: null,
    Category: null,
    Status: null,
    DueDate: new Date(),
    MitigationPlan: "",
  });

  const [RiskLikelihood, RiskImpact, RiskCategory, RiskStatus] = useSelector(
    ({ theme }) => [
      theme.RiskLikelihood.item.Items,
      theme.RiskImpact.item.Items,
      theme.RiskCategory.item.Items,
      theme.RiskStatus.item.Items,
    ]
  );

  useEffect(() => {
    if (data) {
      setInitialValues({
        ...data,
        Likelihood: RiskLikelihood.find(
          (e) => e.Value === data.Likelihood.toString()
        ),
        Impact: RiskImpact.find((e) => e.Value === data.Impact.toString()),
        Category: RiskCategory.find(
          (e) => e.Value === data.Category.toString()
        ),
        Status: RiskStatus.find((e) => e.Value === data.Status.toString()),
        DueDate: new Date(data.DueDate),
      });
    }
    setLoading(false);
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    let body = {
      ...values,
      Likelihood: values.Likelihood.Value,
      Impact: values.Impact.Value,
      Category: values.Category.Value,
      Status: values.Status.Value,
      ID: data ? data.ID : risks?.length + 1,
    };
    if (initiativeData) {
      body = { ...body, InitiativeID: initiativeData.ID, ID: data?.ID };
      try {
        const service = data ? "updateRisk" : "createRisk";
        const res = await api.initiatives[service](body);
        const { StatusCode } = res;
        if (StatusCode === "Success") {
          addToast.success(data ? "toast.successUpdate" : "toast.successSave");
          getData();
          closeModal();
        }
      } catch (error) {
        addToast.error(error?.message, {
          toastId: "get-users-error",
        });
      } finally {
        setSubmitting(false);
      }
    } else if (data) {
      const newData = [...risks];
      const index = risks.findIndex((e) => e.ID === data.ID);
      newData[index] = body;
      setRisks([...newData]);
    } else setRisks([...risks, body]);
    setSubmitting(false);
    closeModal();
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({
        id: data ? "initiative.update-risk" : "initiative.add-risk",
      })}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="risk-form-body"
            >
              <Space direction="vertical" className="container" size="large">
                <Row size="small">
                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({
                      id: "common.risk-name",
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "common.risk-name",
                      }),
                      onChange: (e) => {
                        setFieldValue("EnglishName", e.target.value);
                        setFieldValue("ArabicName", e.target.value);
                      },
                    }}
                    hideErrorMessage
                    required
                  />
                  <div style={{ width: "100%" }} />
                </Row>
              </Space>
              <Space direction="vertical" className="container" size="large">
                <Row size="small">
                  <Field
                    type="select"
                    name="Likelihood"
                    label={intl.formatMessage({ id: "risk.probability" })}
                    inputProps={{
                      options: RiskLikelihood,
                      getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                      getOptionValue: (el) => el.Value,
                      placeholder: intl.formatMessage({
                        id: "risk.probability",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                  <Field
                    type="select"
                    name="Impact"
                    label={intl.formatMessage({ id: "risk.impact" })}
                    inputProps={{
                      options: RiskImpact,
                      getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                      getOptionValue: (el) => el.Value,
                      placeholder: intl.formatMessage({
                        id: "risk.impact",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                  <Field
                    type="select"
                    name="Category"
                    label={intl.formatMessage({ id: "risk.category" })}
                    inputProps={{
                      options: RiskCategory,
                      getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                      getOptionValue: (el) => el.Value,
                      placeholder: intl.formatMessage({
                        id: "risk.category",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="select"
                    name="Status"
                    label={intl.formatMessage({ id: "common.status" })}
                    inputProps={{
                      options: RiskStatus,
                      getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                      getOptionValue: (el) => el.Value,
                      placeholder: intl.formatMessage({
                        id: "common.status",
                      }),
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>
              </Space>
              <Space
                direction="vertical"
                className="container"
                size="large"
                style={{ width: "49.5%" }}
              >
                <Field
                  type="date"
                  name="DueDate"
                  label={intl.formatMessage({ id: "common.due-date" })}
                  inputProps={{
                    placeholderText: intl.formatMessage({
                      id: "common.due-date",
                    }),
                  }}
                  hideErrorMessage
                  fixedLabel
                  required
                />
              </Space>
              <Space direction="vertical" className="container" size="large">
                <Field
                  type="textarea"
                  name="MitigationPlan"
                  label={intl.formatMessage({
                    id: "risk.mitigation-plan",
                  })}
                  inputProps={{
                    placeholder: intl.formatMessage({
                      id: "risk.mitigation-plan",
                    }),
                  }}
                  hideErrorMessage
                  required
                />
              </Space>
              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add",
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default RiskForm;
